import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Chip,
  LinearProgress,
  Typography,
} from '@mui/material';
import {
  Send,
  AttachFile,
  Close,
  Image as ImageIcon,
  Description,
} from '@mui/icons-material';
import { FileAttachmentDialog, ProcessedAttachment } from './FileAttachmentDialog';
import { ChatAttachment } from '../../types';

interface ChatInputProps {
  onSendMessage: (content: string, attachments: ChatAttachment[]) => Promise<void>;
  disabled?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
}) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAttachClick = () => {
    setDialogOpen(true);
  };

  const handleAttachFile = (processedAttachment: ProcessedAttachment) => {
    const attachment: ChatAttachment = {
      fileName: processedAttachment.fileName,
      fileType: processedAttachment.fileType,
      attachmentType: processedAttachment.attachmentType,
      imageData: processedAttachment.imageData,
      textContent: processedAttachment.textContent,
      source: 'user',
    };
    
    setAttachments(prev => [...prev, attachment]);
    setDialogOpen(false);
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, idx) => idx !== index));
  };

  const getAttachmentIcon = (attachment: ChatAttachment) => {
    if (attachment.attachmentType === 'image') {
      return <ImageIcon />;
    }
    return <Description />;
  };

  const handleSend = async () => {
    if (!message.trim() && attachments.length === 0) return;

    const content = message.trim();

    // Clear input immediately
    setMessage('');
    setAttachments([]);

    await onSendMessage(content, attachments);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <FileAttachmentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAttach={handleAttachFile}
      />

      <Paper sx={{ p: 2 }}>
        {attachments.length > 0 && (
          <Box mb={2} display="flex" gap={1} flexWrap="wrap">
            {attachments.map((attachment, idx) => (
              <Chip
                key={idx}
                icon={getAttachmentIcon(attachment)}
                label={attachment.fileName}
                onDelete={() => handleRemoveAttachment(idx)}
                deleteIcon={<Close />}
                size="small"
                color={attachment.attachmentType === 'image' ? 'primary' : 'default'}
              />
            ))}
          </Box>
        )}

        <Box display="flex" gap={1} alignItems="flex-end">
          <IconButton
            onClick={handleAttachClick}
            disabled={disabled}
            color="primary"
          >
            <AttachFile />
          </IconButton>

          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type your message... (Shift+Enter for new line)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={disabled}
            variant="outlined"
            size="small"
          />

          <IconButton
            onClick={handleSend}
            disabled={disabled || (!message.trim() && attachments.length === 0)}
            color="primary"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
              },
            }}
          >
            <Send />
          </IconButton>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          You can attach images, PDFs, PowerPoint, Word, Excel, and plain text files
        </Typography>
      </Paper>
    </>
  );
};

